import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import NavBack from 'dashboard/components/NavBack';
import { Backdrop } from 'dashboard/layouts/IndexViewLayoutV2';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { CustomListRunHistory, ReportTemplateCustomList } from 'dashboard/models/report-template';
import { ReportTemplateVersionCustomList } from 'dashboard/models/report-template-version';
import { RootState } from 'dashboard/redux/store';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

export interface CustomListRunHistoryTableProps {
  reportTemplate: ReportTemplateCustomList;
  reportTemplateVersion: ReportTemplateVersionCustomList;
  historyEntries: CustomListRunHistory[];
}

const statusSticker = (history: CustomListRunHistory) => {
  switch (history.status) {
    case 'complete':
      return <Sticker variant='success'>Complete</Sticker>;
    case 'failed':
      return <Sticker variant='danger'>Failed</Sticker>;
    case 'not_started':
      return <Sticker variant='basic'>Not started</Sticker>;
    case 'queued':
      return <Sticker variant='caution'>Queued</Sticker>;
    case 'running':
      return <Sticker variant='caution'>Running</Sticker>;
  }
};

export const CustomListRunHistoryTable = ({
  reportTemplate,
  reportTemplateVersion,
  historyEntries,
}: CustomListRunHistoryTableProps) => {
  const currentEnvironment = useSelector(
    (state: RootState) => state.currentEnvironment.currentEnvironment,
  );

  return (
    <>
      <FullScreenHeaderContainer>
        <HeaderTitle>
          <NavBack
            to={`/dashboard/report-templates/${reportTemplate.id}/${reportTemplateVersion.id}`}
          />
          <span>{`${reportTemplate.attributes.name} / Run History`}</span>
        </HeaderTitle>
      </FullScreenHeaderContainer>
      <Backdrop>
        <Card>
          <Card.Body>
            <Table>
              <TableHead>
                <TableHeaderCell>Version ID</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Created at</TableHeaderCell>
                <TableHeaderCell>Completed at</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
              </TableHead>
              <TableBody>
                {historyEntries
                  .filter((entry) => entry['environment-token'] === currentEnvironment.id)
                  .map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{entry['report-template-version-token']}</TableCell>
                      <TableCell>{entry['creator-name']}</TableCell>
                      <TableCell>
                        {DateTime.fromISO(entry['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
                      </TableCell>
                      <TableCell>
                        {entry['completed-at']
                          ? DateTime.fromISO(entry['completed-at']).toFormat(DATE_AND_TIME_FORMAT)
                          : '--'}
                      </TableCell>
                      <TableCell>{statusSticker(entry)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card.Body>
        </Card>
      </Backdrop>
    </>
  );
};

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;
